import { z } from "zod";

import { CaloriesEstimate } from "./session.js";
import { slugString } from "./slug.js";
import { WeightSourceEnum } from "./weightSource.js";
import { NAMESPACED_VALUE_MAX } from "./tag.js";
import { Side } from "../side.js";
import {
  SIGNED_WEIGHT_KG_MAX,
  SIGNED_WEIGHT_KG_MIN,
  WEIGHT_KG_MAX,
  WEIGHT_KG_MIN,
} from "../units.js";

// WorkoutSet is a discriminated union over the three logging modes.
// Mirrors the SL convention recorded in memory: omit-vs-zero matters,
// present-0 ≠ absent. Keep these shapes minimal — extra fields go in
// future versions of the schema, not as optional everywhere.
const SetReps = z.number().int().min(0).max(999);
const SetWeightKg = z.number().min(WEIGHT_KG_MIN).max(WEIGHT_KG_MAX); // canonical kg storage; units.ts owns both the conversion contract and the bound
// Bodyweight extra and planner absolute targets allow negative values so
// assisted variants (band-assisted pull-ups, assisted-dip machine) are
// representable without inventing a separate "assistance" type. Range is
// asymmetric: a 500 kg assistance is already absurd, while loaded carries
// can in theory go far above bodyweight.
const SetExtraKg = z.number().min(SIGNED_WEIGHT_KG_MIN).max(SIGNED_WEIGHT_KG_MAX);
const SetP1RMPct = z.number().min(0).max(200); // percentage of 1RM
const SetRPE = z.number().min(1).max(10).optional();
// Reps in Reserve — coach-grade fatigue indicator. Int 0..10, where 0
// means the set went to failure (couldn't have done another rep) and
// higher numbers leave more reps in the tank. Distinct from RPE (more
// objective; the two coexist on a single set: RPE 9 + RIR 1 reads as
// "very hard, one more in the tank"). Drives the Progress dashboard's
// Tier 2 fatigue-drift panel.
const SetRIR = z.number().int().min(0).max(10).optional();
// Phase I additions on every variant. Duration is seconds (kept canonical
// in SI; UI may render mm:ss). Side defaults to "both" for traditional
// barbell work; "left"/"right" only meaningful when the exercise carries
// the unilateral flag. Distance for loaded carries is canonical metres,
// UI converts at the edge for ft.
const SetDurationSec = z.number().int().min(0).max(86400).optional();
const SetSide = Side.optional();
// 200 km ceiling: long outdoor cycling rides routinely pass 10 km (the
// old cap rejected the whole workout — Leonardo's >10 km ride, inbox
// b44abfd0). 200 km still catches a gross extra-digit typo while
// covering any realistic single-bout distance.
const SetDistanceM = z.number().min(0).max(200000);
// A pure label (AP-0p model, 2026-07-06): the set ended in a failed attempt
// (always exactly one). `reps` already holds the clean reps completed — the
// failed rep is decremented at log time — so `fail` changes NO math: volume,
// e1RM and PR all use `reps` like any set. It only records "went to failure"
// for history + the timeline marker (and is a maximal-effort signal for the
// MAX-regression check, like RIR 0). (Old rule, 2026-05-15→2026-07-06: a
// fail was excluded from volume/e1RM — dropped, that lost real reps.)
const SetFail = z.boolean().optional();

// Per-set completion timestamp. Stamped client-side when the user taps
// the "done" button on a set. Drives the workout timeline (gap analysis,
// session-density charts, rest-honesty) and is the trigger point for
// the rest timer + PR check. Optional because legacy rows + a still-
// in-progress draft don't have one — the field is "did the user mark
// this set as concluded yet?", not "does the record exist?". The
// canonical store is ISO 8601 UTC; the form sets it to `new Date()`
// at click time.
const SetCompletedAt = z.iso.datetime().optional();

// Per-set free-text note. Distinct from entry.notes (the header on the
// whole exercise) and from workout.instructions (the session-level cue).
// Mirrors LogSet.note storage. Set 0 shares storage with entry.notes
// when entry.notes is set (entry.notes wins). 500-char ceiling matches
// the entry-level cap; longer cues go on a coaching message, not a row.
const SetNote = z.string().max(500).optional();

// T8 dropset chain. Two variants in the order Antenore uses:
//   - "classical" — peso scende, max reps, no rest. Each step typically
//     drops 10-30% weight and repeats to fatigue.
//   - "myorep" — stesso peso, fewer reps, no rest (rest-pause). 10-20s
//     between mini-sets; 3-5 of them is the common pattern.
// The chain attaches to a single parent WorkoutSet (weighted or
// bodyweight). Each step records reps and an optional weight/extra: for
// myorep the weight typically stays identical to the parent, so omitting
// it reads as "same load". For classical, every step ought to carry a
// weight, but the schema keeps it optional so a half-logged set doesn't
// crash on save. Variant lives on the chain (uniform across steps) — a
// real workout never mixes the two within a single drop chain.
const DropsetVariant = z.enum(["classical", "myorep"]);
const DropsetStep = z.object({
  reps: SetReps,
  weight: SetWeightKg.optional(),
  extra: SetExtraKg.optional(),
  // Per-step completion stamp (Logger redesign slice 4). Load-bearing
  // identical with the logSet.ts DropsetStep — they describe the same
  // domain object, and the comment on logSet.ts:29 explicitly says
  // both must move together. Optional for back-compat with rows
  // written before this field existed.
  completedAt: z.iso.datetime().optional(),
});
const Dropset = z.object({
  variant: DropsetVariant,
  steps: z.array(DropsetStep).min(1).max(8),
});
const SetDropset = Dropset.optional();

export const WorkoutSetP1RM = z.object({
  type: z.literal("p1rm"),
  reps: SetReps,
  p1rm: SetP1RMPct,
  rfl: z.boolean(), // suggestion source flag; see SL feedback_rfl memory
  rpe: SetRPE,
  rir: SetRIR,
  duration: SetDurationSec,
  side: SetSide,
  fail: SetFail,
  completedAt: SetCompletedAt,
  note: SetNote,
});

// Bilateral toggle (1.45.0). 1 = single-implement load (one DB row,
// one-arm KB swing); 2 = paired implements moving together (two-DB
// row, double KB swing). The displayed `weight` is always per-
// implement; the volume math reads `loadedSides` and multiplies.
// Rows without the field read as ×1 (setVolume.ts setLoadMultiplier —
// the "default to 2" a stale version of this comment described was
// the v2.29.0 double-count bug, never the contract). Optional for
// backward-compat; clients ≥3.99.18 send it explicitly on every
// weighted/bodyweight set so the API's catalog stamp (IB-8 Slice 2)
// can tell "chose bilateral" from "forgot the field".
const SetLoadedSides = z.union([z.literal(1), z.literal(2)]).optional();

// Provenance of the prefilled weight, carried from the logger draft
// (DraftSet.weightSource) through to LogSet.weightSource at save so the
// post-session deviation math reads a stable label. Optional + additive:
// legacy payloads without it still validate, and the LogSet schema treats
// absence as 'manual'. ⛔ It no longer "mirrors logSet.ts" — a mirror is a
// copy that drifts; both now read the same enum (schemas/weightSource.ts).
const SetWeightSource = WeightSourceEnum.optional();

// "This set went to the limit on the open variable." Not a set *type*:
// the athlete fixes one variable and opens another, then reports what the
// open one turned out to be. Strength: load fixed, reps open. Isometric
// hold: load fixed, time open. Cardio: time or distance fixed, the other
// open. One single flag covers all three — there must never be an
// `amtap`/`amdap` sibling; the next slice extends THIS field to the timed
// and cardio variants, which is why the comment describes them already.
// Wired here only on weighted/bodyweight because that is what the logger
// chip drives today (the cardio/timed surfaces are still an open product
// decision). Optional + omit-the-default: it rides only as `true`, so a
// legacy row without it reads as "not an AMRAP" with no backfill.
// Mirrors logSet.ts exactly.
const SetAmrap = z.boolean().optional();

// #353: a set of a MAX calibration ramp. The post-save MAX proposal reads it:
// only these sets may propose a FIRST MAX. Weighted only, omit-the-default
// like `amrap`. Mirrors logSet.ts.
const SetCalibration = z.boolean().optional();

export const WorkoutSetWeighted = z.object({
  type: z.literal("weighted"),
  reps: SetReps,
  weight: SetWeightKg,
  loadedSides: SetLoadedSides,
  rpe: SetRPE,
  rir: SetRIR,
  duration: SetDurationSec,
  side: SetSide,
  fail: SetFail,
  dropset: SetDropset,
  completedAt: SetCompletedAt,
  note: SetNote,
  weightSource: SetWeightSource,
  amrap: SetAmrap,
  calibration: SetCalibration,
});

export const WorkoutSetBodyweight = z.object({
  type: z.literal("bodyweight"),
  reps: SetReps,
  extra: SetExtraKg.optional(),
  // Unilateral bodyweight (pistol, single-leg RDL, one-arm push-up): one
  // side logged, both performed → volume ×2. Same semantics + read-default
  // (1) as the weighted variant; persisted only when 2.
  loadedSides: SetLoadedSides,
  rpe: SetRPE,
  rir: SetRIR,
  duration: SetDurationSec,
  side: SetSide,
  fail: SetFail,
  dropset: SetDropset,
  completedAt: SetCompletedAt,
  note: SetNote,
  weightSource: SetWeightSource,
  amrap: SetAmrap,
});

// Loaded carry: distance is canonical (metres), reps optional (some carries
// log only distance + duration, others count steps as reps), weight is
// total external load.
export const WorkoutSetLoadedCarry = z.object({
  type: z.literal("loaded-carry"),
  weight: SetWeightKg,
  distanceM: SetDistanceM,
  reps: SetReps.optional(),
  rpe: SetRPE,
  rir: SetRIR,
  duration: SetDurationSec,
  side: SetSide,
  fail: SetFail,
  completedAt: SetCompletedAt,
  note: SetNote,
});

// Cardio set (1.62.0). Duration is the primary metric and required;
// distance/load/HR/calories are optional so the same shape covers a
// continuous bout (30-min treadmill run with distance + HR), an
// interval set (1 of 5 × 400m, just duration + distance), and a sled
// push (duration + weight, no distance because metres on the sled
// floor are a coaching detail not a logged metric for most). Hybrid
// design picked 2026-05-17 with Antenore: 1 set per cardio session by
// default (lifter adds more via the same add_set affordance as
// strength) so intervals stay representable without forcing multi-set
// UX on a 30-min steady run.
const CardioDurationSec = z.number().int().min(1).max(86400);
const SetHrBpm = z.number().int().min(30).max(250).optional();
const SetKcal = z.number().min(0).max(5000).optional();

export const WorkoutSetCardio = z.object({
  type: z.literal("cardio"),
  duration: CardioDurationSec,
  distanceM: SetDistanceM.optional(),
  weight: SetWeightKg.optional(),
  avgHrBpm: SetHrBpm,
  calories: SetKcal,
  rpe: SetRPE,
  rir: SetRIR,
  fail: SetFail,
  completedAt: SetCompletedAt,
  note: SetNote,
});

// Timed (isometric) set — plank, wall sit, bird-dog hold, the physiotherapy
// isometrics (inbox 3dd20b1c). Duration held is the only metric; no reps,
// weight, distance or HR. Reuses the cardio second-range (1..86400). Effort +
// fail + note ride along like every other set so a hard plank can carry an RIR.
export const WorkoutSetTimed = z.object({
  type: z.literal("timed"),
  duration: CardioDurationSec,
  // Optional added load — weighted plank, weighted dip-hold, weighted dead
  // hang (Antenore 2026-06-05). Informational on the set; timed sets still
  // contribute 0 to reps×weight volume (no reps).
  weight: SetWeightKg.optional(),
  rpe: SetRPE,
  rir: SetRIR,
  fail: SetFail,
  completedAt: SetCompletedAt,
  note: SetNote,
});

export const WorkoutSet = z.discriminatedUnion("type", [
  WorkoutSetP1RM,
  WorkoutSetWeighted,
  WorkoutSetBodyweight,
  WorkoutSetLoadedCarry,
  WorkoutSetCardio,
  WorkoutSetTimed,
]);
export type WorkoutSet = z.infer<typeof WorkoutSet>;

// T8 special-set group. Superset (2+ exercises alternating, no rest in
// between) and Circuit (3+ exercises in rotation, rest at end of round)
// share the same shape: a stable id linking the participating entries
// and a kind tag. Dropsets aren't here — those live on individual sets,
// not across entries (see WorkoutSet.dropset above).
export const WorkoutEntryGroup = z.object({
  id: z.string().min(1).max(64),
  kind: z.enum(["superset", "circuit"]),
  // Optional user label for the group (inbox 963ca6f2 §1: "il nome del
  // superset deve poter essere modificato"). Defaults client-side to a short
  // auto-abbreviation of the members; absent = render "Superset N". Additive
  // + backward-compat — old rows without it keep the numbered label.
  name: z.string().trim().min(1).max(60).optional(),
});
export type WorkoutEntryGroup = z.infer<typeof WorkoutEntryGroup>;

// `exerciseName` is a de-normalised display snapshot. Until the exercise
// catalog ships, the client sends a slugified `exerciseId` and the same
// human-readable name; once we have a catalog, `exerciseId` will reference
// it but `exerciseName` stays as a frozen-at-write copy so historical
// records keep displaying their original wording even if the catalog
// entry is later renamed.
export const WorkoutExerciseEntry = z.object({
  exerciseId: slugString(64, "must be a slug (a-z, 0-9, dashes)"),
  exerciseName: z.string().min(1).max(120).optional(),
  sets: z.array(WorkoutSet).min(1).max(50),
  notes: z.string().max(500).optional(),
  // Meant as a write-time snapshot of the exercise's tags, but no writer
  // ever filled it. PG-57: readers resolve the tags from the catalog by
  // exerciseId (bucketTagVolumeByWeek); this is only their fallback.
  tags: z.array(slugString(64)).max(20).optional(),
  // EQ-1 — WHICH machine, and WHERE. Two dedicated fields, deliberately
  // not folded into `tags` above, for two independent reasons:
  //   1. They cannot go there. `tags` is slugString, whose regex rejects
  //      the colon, so a namespaced `equipment-instance:<v>` would fail
  //      validation outright — not merely sit there untidily.
  //   2. They must not go there. `tags` mirrors the exercise's CATALOG
  //      tags, and bucketTagVolumeByWeek (progress.ts) aggregates volume-by-tag
  //      off them. An instance tag
  //      landing here would split already-shipped volume charts along the
  //      machine axis — the one axis Antenore decided stays whole
  //      ("il progress del muscolo è uno solo", 2026-08-12).
  // Values are the BARE slug; the `equipment-instance:` / `location:`
  // namespace is applied at the write seam (workoutSplit) so the field
  // and the tag can't drift. Cap is derived — see NAMESPACED_VALUE_MAX.
  equipmentInstance: slugString(NAMESPACED_VALUE_MAX).optional(),
  location: slugString(NAMESPACED_VALUE_MAX).optional(),
  // T8 group membership. When two or more entries in the same workout
  // share a `group.id`, they form a superset/circuit. Validation lives
  // on the client (must be ≥2 entries per id, all sharing the same
  // kind) — the server stores the field as-is.
  group: WorkoutEntryGroup.optional(),
});
export type WorkoutExerciseEntry = z.infer<typeof WorkoutExerciseEntry>;

export const Workout = z.object({
  id: z.string().min(1),
  profileId: z.string().min(1),
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime().optional(),
  // Optional human label for the workout — "Lower #1 Quad-Dominant",
  // "Push A", etc. Mirrors the SL CSV `workout` column and the new
  // Session.name field, so importer + native logger can both round-trip
  // a discoverable label. Cap matches Session.name (120 chars).
  name: z.string().min(1).max(120).optional(),
  // Reads tolerate empty entries (a session row whose sets were never
  // logged or were cascaded) — the assembler used to forge a placeholder
  // to satisfy `.min(1)`, which invented data. NewWorkout (the WRITE
  // shape) re-extends this with `.min(1)` so writers still can't
  // create a genuinely empty workout.
  entries: z.array(WorkoutExerciseEntry).min(0).max(40),
  // Lineage: when the athlete saves a workout that started from a plan we
  // stamp the plan id here. Lets the UI show "from plan X" and the API mark
  // the plan row completed without inferring the link from timestamps.
  // Cap unified with session.fromPlanId / logSet.fromPlanId at 256 so
  // composite ids like prog/<uuid>/w<n>/s<n>/<slug> never exceed any of
  // the three storage surfaces.
  fromPlanId: z.string().min(1).max(256).optional(),
  // JL's own workout calorie estimate (HC-5). The logger computes it at save
  // from the sets + the athlete's bodyweight, and splitWorkout forwards it to
  // the Session (where it lives beside the measured `calories`, never
  // overwriting it). Session-level, not per-set. Absent ⇒ nothing estimable
  // (no bodyweight, or a strength session with no end time). Same shape as
  // Session.caloriesEstimate — see session.ts.
  caloriesEstimate: CaloriesEstimate.optional(),
  // Measured session-level energy + effort, carried READ-ONLY from the Session
  // so the mobile edit view can show the enriched value instead of re-estimating
  // (HC-8 B2c). Same shapes as Session.calories/avgHrBpm/peakHrBpm/externalOrigin.
  // These are provenance display metadata: `assembleWorkout` copies them in on
  // read, but `splitWorkout` (write) does NOT forward them — they come from HC
  // enrichment, not the logger. A correction PATCHes the session in place
  // without them (correctWorkout, #38), so the server keeps them.
  calories: z.number().int().min(0).max(20000).optional(),
  avgHrBpm: z.number().int().min(30).max(250).optional(),
  peakHrBpm: z.number().int().min(30).max(250).optional(),
  externalOrigin: z.string().min(1).max(256).optional(),
  // Workout-level coach instructions. Distinct from per-set notes (kept on
  // each WorkoutSet) and from slot.instructions on a program: this is the
  // free-text the coach pins on the whole session — focus, intent, what
  // to skip if time-pressed. The third surface in the T11 comments
  // redesign (set-level structured data + slot/workout instructions +
  // per-set free-text note).
  instructions: z.string().max(1000).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  // Read-only, from the Session: the version a correction is built on (#314).
  loggerUpdatedAt: z.iso.datetime().optional(),
});
export type Workout = z.infer<typeof Workout>;

// Client → server payload. Server fills profileId/createdAt/updatedAt.
// `startedAt` stays client-supplied so users can back-log a session they
// did earlier today (or yesterday) without us forging a server clock.
//
// `id` is optional but, when supplied, must be a UUID. Offline-first
// mobile clients and the CSV log importer always supply it so the same
// payload re-sent after a flaky connection reaches the server with the
// same identity. The server treats a duplicate id (same user) as a
// 409 conflict — never silent overwrite. When absent, the server
// stamps a `randomUUID()` so existing online clients keep working
// without changes.
export const NewWorkout = Workout.omit({
  id: true,
  profileId: true,
  createdAt: true,
  updatedAt: true,
  loggerUpdatedAt: true,
}).extend({
  id: z.uuid().optional(),
  // Re-tighten the read-side relax: writes must still carry ≥1 entry.
  entries: z.array(WorkoutExerciseEntry).min(1).max(40),
  // Optional carrier for the original creation time on an EDIT. A
  // correction rewrites the session in place and never sends it (#38), so
  // the server keeps its own. New workouts omit it → server stamps `now`.
  createdAt: z.iso.datetime().optional(),
});
export type NewWorkout = z.infer<typeof NewWorkout>;
